import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { formatDisplayText } from '../../utils/text-case.utils';

/**
 * One button inside the card. A button with neither `navigateTo` nor
 * `externalUrl` renders normally but does nothing on click — that is how a
 * not-yet-implemented choice (e.g. OEM Price File) stays a placeholder without
 * looking different from the live one.
 */
export interface ActionChoiceCardAction {
  label?: string;
  key?: string;
  navigateTo?: string;
  queryParams?: Record<string, any>;
  navigationState?: Record<string, any>;
  externalUrl?: string;
  target?: string;
  disabled?: boolean;
  /** Mirrors ButtonComponent's variants: 'danger' (default) | 'secondary' | 'light'. */
  styleClass?: string;
}

/**
 * Optional "gate" tile (icon + title + description) shown in front of the
 * actions. Clicking it reveals the actions in place — no route change, no
 * overlay — so a module's landing page can read as two beats (tile, then
 * choices) without leaving the page.
 */
export interface ActionChoiceCardEntry {
  icon?: string;
  title?: string;
  description?: string;
  iconColor?: string;
  iconBackground?: string;
}

/**
 * Landing card: a single centred card holding a short list of navigation
 * buttons — the "which flow do you want?" entry point of a module. Title,
 * buttons, routes and sizing all come from the dashboard JSON, so a module gets
 * a landing page by adding a config file rather than a component.
 */
@Component({
  selector: 'app-action-choice-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './action-choice-card.component.html',
  styleUrl: './action-choice-card.component.scss',
})
export class ActionChoiceCardComponent {
  @Input() data: any;

  /** Once the entry tile is clicked (or if there's no entry tile), the actions show. */
  revealed = false;

  constructor(private router: Router) {}

  get entryCard(): ActionChoiceCardEntry | null {
    return this.data?.entryCard || null;
  }

  /** The entry tile stays visible once revealed — it just fades behind the
   *  popup that layers on top of it, rather than being removed. */
  get showEntry(): boolean {
    return !!this.entryCard;
  }

  get showActions(): boolean {
    return !this.entryCard || this.revealed;
  }

  /** True once the popup is layered on top of a (now faded) entry tile. */
  get isOverlay(): boolean {
    return !!this.entryCard && this.revealed;
  }

  get entryStyles(): Record<string, string | null> {
    return {
      '--entry-icon-color': this.entryCard?.iconColor || null,
      '--entry-icon-bg': this.entryCard?.iconBackground || null,
    };
  }

  onEntryClick(): void {
    this.revealed = true;
  }

  onEntryKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onEntryClick();
  }

  get title(): string {
    return String(this.data?.title ?? this.data?.label ?? '').trim();
  }

  get subtitle(): string {
    return String(this.data?.subtitle ?? this.data?.description ?? '').trim();
  }

  get actions(): ActionChoiceCardAction[] {
    return Array.isArray(this.data?.actions) ? this.data.actions : [];
  }

  /** Buttons sit side by side by default; `"actionsLayout": "column"` stacks them. */
  get isColumnLayout(): boolean {
    return String(this.data?.actionsLayout || '').trim().toLowerCase() === 'column';
  }

  get cardStyles(): Record<string, string | null> {
    return {
      '--choice-card-width': this.data?.cardWidth || null,
      '--choice-card-padding': this.data?.cardPadding || null,
      '--choice-card-radius': this.data?.cardRadius || null,
      '--choice-card-gap': this.data?.actionGap || null,
      '--choice-action-width': this.data?.buttonWidth || null,
    };
  }

  /** Per UI Standards (Point 3): Capital Case unless the config overrides it. */
  actionLabel(action: ActionChoiceCardAction): string {
    return formatDisplayText(action?.label, this.data?.textCase || 'capital');
  }

  actionClasses(action: ActionChoiceCardAction): Record<string, boolean> {
    const style = String(action?.styleClass || 'danger').trim();
    return {
      'choice-action-danger': style === 'danger',
      'choice-action-secondary': style === 'secondary',
      'choice-action-light': style === 'light',
      'is-disabled': !!action?.disabled,
    };
  }

  onActionClick(action: ActionChoiceCardAction): void {
    if (!action || action.disabled) return;

    const route = String(action.navigateTo || '').trim();
    if (route) {
      this.router.navigate([route], {
        queryParams: action.queryParams,
        state: action.navigationState,
      });
      return;
    }

    const externalUrl = String(action.externalUrl || '').trim();
    if (externalUrl) {
      window.open(externalUrl, action.target || '_self');
    }
  }
}
